import React, { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '../styled-system/css/index';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const pageStyle = css({
  maxW: '900px',
  mt: '40px',
  mb: '40px',
  ml: 'auto',
  mr: 'auto',
  p: '0 20px',
  color: '#111827',
});

const panelStyle = css({
  bg: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  p: '20px',
  color: '#111827',
});

const tableStyle = css({
  w: '100%',
  borderCollapse: 'collapse',
  m: '18px 0',
});

const thStyle = css({
  textAlign: 'left',
  p: '10px',
  borderBottom: '1px solid #e5e7eb',
  color: '#6b7280',
});

const tdStyle = css({
  p: '10px',
  borderBottom: '1px dashed #e5e7eb',
});

const formatCents = (amount, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((amount || 0) / 100);

const formatAddress = (shipping) => {
  if (!shipping) return [];
  const address = shipping?.address || shipping;
  return [
    shipping?.name || address.name,
    address.company,
    address.line1 || address.street1,
    address.line2 || address.street2,
    [address.city, address.state, address.postal_code || address.zip].filter(Boolean).join(', '),
    address.country,
  ].filter(Boolean);
};

const OrderSuccess = () => {
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const didClearCart = useRef(false);
  const orderToken = useMemo(() => {
    const hashToken = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('order_token');
    const queryToken = new URLSearchParams(window.location.search).get('order_token');
    const token = hashToken || queryToken || window.sessionStorage.getItem('eziblank_order_token');
    if (token) {
      window.sessionStorage.setItem('eziblank_order_token', token);
      window.history.replaceState(null, '', window.location.pathname);
    }
    return token;
  }, []);

  useEffect(() => {
    if (didClearCart.current) return;
    didClearCart.current = true;
    clearCart();
  }, [clearCart]);

  useEffect(() => {
    if (!orderToken) {
      setError('The secure order link was not provided.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer;
    const loadOrder = async (attempt = 0) => {
      timer = undefined;
      try {
        const res = await fetch('/api/orders/lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ token: orderToken }),
        });
        const data = await res.json();
        if (res.status === 202 && attempt < 10) {
          timer = window.setTimeout(() => loadOrder(attempt + 1), 1500);
          return;
        }
        if (!res.ok) throw new Error(data.message || 'Failed to load invoice');
        if (!cancelled) setOrder(data.order);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message);
      } finally {
        if (!cancelled && !timer) setLoading(false);
      }
    };
    loadOrder();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [orderToken]);

  if (loading) {
    return (
      <div className={pageStyle}>
        <h2 style={{ color: '#197bbd' }}>Loading Invoice</h2>
        <p>Confirming your order details.</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className={pageStyle}>
        <h2 style={{ color: '#197bbd' }}>Payment Successful</h2>
        <p>{error || 'Your payment was successful, but the invoice could not be loaded.'}</p>
        <p>
          Please save your order number from Stripe and <Link to="/contact">contact us</Link> if you need help.
        </p>
      </div>
    );
  }

  const shippingLines = formatAddress(order.shipping);

  return (
    <div className={pageStyle}>
      <h2 style={{ color: '#197bbd' }}>Payment Successful</h2>
      <div className={panelStyle}>
        <h3 style={{ marginTop: 0 }}>Invoice</h3>
        <p><strong>Order Number:</strong> {order.id}</p>
        <p><strong>Customer:</strong> {order.customer_name || 'Customer'}{order.customer_email ? ` (${order.customer_email})` : ''}</p>
        {order.customer_phone ? <p><strong>Phone:</strong> {order.customer_phone}</p> : null}

        <table className={tableStyle}>
          <thead>
            <tr>
              <th className={thStyle}>Item</th>
              <th className={thStyle}>Qty</th>
              <th className={thStyle} style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, index) => (
              <tr key={`${item.description}-${index}`}>
                <td className={tdStyle}>{item.description}</td>
                <td className={tdStyle}>{item.quantity}</td>
                <td className={tdStyle} style={{ textAlign: 'right' }}>{formatCents(item.amount_total, order.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginLeft: 'auto', maxWidth: 320, textAlign: 'right' }}>
          <div>Subtotal: {formatCents(order.subtotal, order.currency)}</div>
          <div>Shipping: {formatCents(order.shipping_total, order.currency)}</div>
          <div>Tax: {formatCents(order.tax, order.currency)}</div>
          <p style={{ fontWeight: 700 }}>Total: {formatCents(order.total, order.currency)}</p>
        </div>

        {shippingLines.length ? (
          <div>
            <h4>Shipping Address</h4>
            {shippingLines.map((line) => <div key={line}>{line}</div>)}
          </div>
        ) : null}

        {order.receipt_url ? (
          <p><a href={order.receipt_url} target="_blank" rel="noreferrer">View Stripe receipt</a></p>
        ) : null}
      </div>
      <p style={{ marginTop: 18 }}>
        A copy of this invoice will be emailed to the customer and fulfillment team.
      </p>
    </div>
  );
};

export default OrderSuccess;
